import React from "react";
import { Spin } from "antd";
import { HolderOutlined } from "@ant-design/icons";

/**
 * DashboardWidget — reusable card shell for every dashboard widget.
 *
 * Props:
 *   title      string    — widget heading
 *   subtitle   string    — muted subheading
 *   accent     string    — hex colour for the left border accent strip + header tint
 *   loading    bool
 *   actions    ReactNode — top-right controls
 *   children   ReactNode
 *   dragHandle bool
 *   style      object
 *   bodyStyle  object
 *   noPad      bool      — remove content padding
 */
function DashboardWidget({
  title,
  subtitle,
  accent,
  loading = false,
  actions,
  children,
  dragHandle = false,
  style = {},
  bodyStyle = {},
  noPad = false,
}) {
  // Derive a very light tint from the accent for the header background
  const headerBg = accent ? `${accent}0d` : "transparent"; // 5% opacity

  return (
    <div
      style={{
        background: "var(--color-background-primary)",
        border: "0.5px solid var(--color-border-tertiary)",
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        // Subtle shadow like the screenshot reference
        boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
        // Left accent strip
        borderLeft: accent
          ? `3px solid ${accent}`
          : "0.5px solid var(--color-border-tertiary)",
        ...style,
      }}
    >
      {/* Header */}
      {(title || actions) && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "13px 16px 11px",
            background: headerBg,
            borderBottom: "0.5px solid var(--color-border-tertiary)",
            gap: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              flex: 1,
              minWidth: 0,
            }}
          >
            {dragHandle && (
              <HolderOutlined
                style={{
                  color: "var(--color-text-secondary)",
                  cursor: "grab",
                  marginTop: 2,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              {title && (
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: 1.3,
                    color: accent || "var(--color-text-primary)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {title}
                </div>
              )}
              {subtitle && (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--color-text-secondary)",
                    marginTop: 2,
                  }}
                >
                  {subtitle}
                </div>
              )}
            </div>
          </div>
          {actions && <div style={{ flexShrink: 0 }}>{actions}</div>}
        </div>
      )}

      {/* Body */}
      <div
        style={{
          flex: 1,
          padding: noPad ? 0 : "14px 16px",
          position: "relative",
          ...bodyStyle,
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 100,
            }}
          >
            <Spin size="small" />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export default DashboardWidget;
